import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { EDITORIAL } from "@/data/site";
import { useSeo } from "@/hooks/useSeo";

const ABOUT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About HYEAMAN — Meet the World",
  url: "https://hyeamanbrand.com/about",
  description:
    "Hyeaman is a traditional Ghanaian name meaning \"Meet the World.\" A global luxury streetwear collective, enforced by the Street Sheriff, blending cultures and nationalities so nobody is left behind.",
  mainEntity: {
    "@type": "Organization",
    name: "HYEAMAN",
    alternateName: "Street Sheriff",
    url: "https://hyeamanbrand.com",
    slogan: "Wear Confidence · No One Is Left Behind",
  },
};

export function About() {
  useSeo({
    title: "About HYEAMAN — Meet the World, Rooted in Ghana",
    description:
      "Hyeaman means \"Meet the World.\" Discover our Ghanaian roots and the Street Sheriff — global luxury streetwear blending cultures and nationalities so nobody is left behind.",
    path: "/about",
    jsonLd: ABOUT_JSONLD,
  });
  return (
    <div data-testid="about-page">
      <section className="relative flex min-h-[60vh] items-end overflow-hidden">
        <img src={EDITORIAL.tieMan} alt="HYEAMAN — Meet the World" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 sm:px-8">
          <p className="overline-label mb-3">The HYEAMAN Story · Street Sheriff</p>
          <h1 className="font-black uppercase tracking-tight text-cream text-5xl sm:text-6xl">Meet the <span className="font-serif font-normal italic text-gold">World</span></h1>
          <p className="mt-4 font-mono text-[0.7rem] uppercase tracking-[0.35em] text-gold/70" data-testid="about-slogans">Wear Confidence · No One Is Left Behind</p>
        </div>
      </section>

      <div className="mx-auto max-w-3xl px-4 py-20 sm:px-8">
        {/* OUR ROOTS */}
        <div data-testid="about-roots">
          <p className="overline-label mb-3">Our Roots</p>
          <p className="font-serif text-2xl italic leading-relaxed text-cream">Hyeaman is a traditional Ghanaian name that translates to “Meet the World” or “Meet the Nations.” It is more than just a name — it is our founding blueprint and our lifelong mission.</p>
          <div className="mt-8 space-y-6 text-cream/70">
            <p>We started this brand to create a global canvas where all cultures, nationalities, backgrounds, and personalities seamlessly blend together. In a world that often tries to draw lines and build walls, Hyeaman is here to erase them.</p>
            <p>We believe that fashion should never alienate; it should integrate. No matter where you come from, what language you speak, or how you express yourself, you have a home here. <span className="text-gold">Our clothing is designed so that nobody feels left behind.</span></p>
          </div>
        </div>

        {/* THE STREET SHERIFF */}
        <div className="mt-16 border-t border-white/10 pt-16" data-testid="about-street-sheriff">
          <p className="overline-label mb-3">The Street Sheriff</p>
          <h2 className="font-black uppercase tracking-tight text-cream text-3xl sm:text-4xl">Guardians of the Global Collective</h2>
          <p className="mt-6 font-serif text-xl italic text-gold" data-testid="about-second-name">Street Sheriff is the second name of HYEAMAN.</p>
          <div className="mt-8 space-y-6 text-cream/70">
            <p>To bring this vision to life on the asphalt, we introduced our second name: Street Sheriff. In the concrete jungle, the Street Sheriff isn’t here to enforce division — they are here to protect unity.</p>
            <p>The Street Sheriff is the guardian of our global melting pot. It represents the bold authority, the gritty resilience, and the raw energy required to claim the streets for everyone.</p>
            <p>Together, Hyeaman and Street Sheriff represent a new world order in streetwear. One provides the global soul, and the other provides the urban armor.</p>
          </div>
          <p className="mt-8 font-serif text-xl italic text-gold">Different backgrounds. Same streets. Welcome to the Nations.</p>
          <Link to="/street-sheriff" data-testid="about-ss-link" className="mt-6 inline-block text-xs font-bold uppercase tracking-widest text-gold underline-offset-4 hover:underline">Explore the Street Sheriff collection →</Link>
        </div>

        {/* FOLLOW THE STORY */}
        <div className="mt-16 border-t border-white/10 pt-16" data-testid="about-social">
          <p className="overline-label mb-6">Follow the Story</p>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="border border-white/10 bg-ink p-6" data-testid="about-social-instagram">
              <p className="mb-3 text-xs uppercase tracking-widest text-gold">Instagram</p>
              <p className="text-sm leading-relaxed text-cream/70">HYEAMAN 🌍 Meet the world. Enforced by the Street Sheriff. Blending cultures, nationalities, and styles so nobody gets left behind.</p>
            </div>
            <div className="border border-white/10 bg-ink p-6" data-testid="about-social-tiktok">
              <p className="mb-3 text-xs uppercase tracking-widest text-gold">TikTok</p>
              <p className="text-sm leading-relaxed text-cream/70">Hyeaman (n.): To Meet the Nations. A global collective protected by the Street Sheriff. No borders. No boxes. Everyone belongs on these streets.</p>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-10 text-center">
          {[["5", "Houses"], ["1", "World"], ["24k", "Gold Detailing"]].map(([n, l]) => (
            <div key={l}><p className="font-serif text-3xl text-gold">{n}</p><p className="mt-1 text-xs uppercase tracking-widest text-cream/50">{l}</p></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/contact", form); toast.success("Message received. We'll be in touch."); setForm({ name: "", email: "", message: "" }); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <div className="mx-auto max-w-2xl px-4 py-20 sm:px-8" data-testid="contact-page">
      <p className="overline-label mb-2">Client Care</p>
      <h1 className="mb-8 font-black uppercase tracking-tight text-cream text-4xl">Contact Us</h1>
      <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
        <input required placeholder="Name" value={form.name} onChange={set("name")} data-testid="contact-name" className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream outline-none focus:border-gold" />
        <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="contact-email" className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream outline-none focus:border-gold" />
        <textarea required placeholder="How can we help?" rows={5} value={form.message} onChange={set("message")} data-testid="contact-message" className="w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream outline-none focus:border-gold" />
        <button disabled={loading} data-testid="contact-submit" className="bg-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-black transition hover:bg-gold-bright">{loading ? "Sending…" : "Send Message"}</button>
      </form>
    </div>
  );
}

function LegalPage({ title, sections, testid }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-20 sm:px-8" data-testid={testid}>
      <p className="overline-label mb-2">HYEAMAN</p>
      <h1 className="mb-10 font-black uppercase tracking-tight text-cream text-4xl">{title}</h1>
      <div className="space-y-8">
        {sections.map((s, i) => (
          <div key={i}>
            <h2 className="mb-2 font-serif text-xl text-gold">{s.h}</h2>
            <p className="text-sm leading-relaxed text-cream/70">{s.p}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export const Shipping = () => <LegalPage testid="shipping-page" title="Shipping" sections={[
  { h: "Delivery Times", p: "Canadian orders arrive within 2–4 working days. International delivery takes 5–10 working days depending on destination and customs." },
  { h: "Complimentary Shipping", p: "Enjoy free standard shipping on all Canadian orders of C$250 or more; orders below this threshold incur a flat C$15 shipping fee. Complimentary international shipping applies on orders of C$450 or more — international orders below this threshold are charged at rates calculated at checkout." },
  { h: "Tracking", p: "Once dispatched, you will receive a tracking code by email. You can also view live status in your member order history." },
]} />;

export const Returns = () => <LegalPage testid="returns-page" title="Returns" sections={[
  { h: "30-Day Returns", p: "Unworn pieces with original tags may be returned within 30 days of delivery for a full refund." },
  { h: "How to Return", p: "Contact client care with your order number to receive a prepaid returns label. Black Label numbered pieces are final sale." },
  { h: "Refunds", p: "Refunds are processed within 5 working days of receiving your return, to the original payment method." },
]} />;

export const Privacy = () => <LegalPage testid="privacy-page" title="Privacy Policy" sections={[
  { h: "Data We Collect", p: "We collect only the information necessary to fulfil your orders and improve your experience: name, email, delivery address and order history." },
  { h: "How We Use It", p: "Your data is used solely to process orders, provide client care and — with consent — send you editorial updates. We never sell your data." },
  { h: "Your Rights", p: "You may request access to, correction of, or deletion of your personal data at any time by contacting client care." },
]} />;

export const Terms = () => <LegalPage testid="terms-page" title="Terms & Conditions" sections={[
  { h: "Orders", p: "All orders are subject to acceptance and availability. Prices and stock are confirmed at checkout from our live inventory." },
  { h: "Intellectual Property", p: "The HYEAMAN crown, monogram and all brand assets are protected trademarks and may not be reproduced without permission." },
  { h: "Liability", p: "HYEAMAN is not liable for delays caused by third-party carriers or events beyond our reasonable control." },
]} />;

export function SizeGuide() {
  const rows = [["XS", "34-36", "28-30", "34-36"], ["S", "36-38", "30-32", "36-38"], ["M", "38-40", "32-34", "38-40"], ["L", "40-42", "34-36", "40-42"], ["XL", "42-44", "36-38", "42-44"]];
  return (
    <div className="mx-auto max-w-3xl px-4 py-20 sm:px-8" data-testid="size-guide-page">
      <p className="overline-label mb-2">Fit & Measurements</p>
      <h1 className="mb-10 font-black uppercase tracking-tight text-cream text-4xl">Size Guide</h1>
      <table className="w-full border border-white/10 text-sm">
        <thead><tr className="bg-ink text-gold">{["Size", "Chest (in)", "Waist (in)", "Hip (in)"].map((h) => <th key={h} className="border border-white/10 px-4 py-3 text-left font-mono text-xs uppercase tracking-widest">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r[0]} className="text-cream/80">{r.map((c, i) => <td key={i} className="border border-white/10 px-4 py-3">{c}</td>)}</tr>)}</tbody>
      </table>
      <p className="mt-6 text-xs text-cream/50">Prince & Princess pieces follow age-based sizing (4Y–12Y). Measurements are approximate — for a relaxed fit, size up.</p>
    </div>
  );
}

export function CheckoutSuccess() {
  const [params] = useSearchParams();
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center" data-testid="checkout-success">
      <CheckCircle2 size={56} className="text-gold" />
      <h1 className="mt-6 font-black uppercase tracking-tight text-cream text-4xl">Order Confirmed</h1>
      <p className="mt-3 text-cream/60">Thank you for choosing HYEAMAN.</p>
      {params.get("order") && <p className="mt-2 font-mono text-gold" data-testid="success-order-number">{params.get("order")}</p>}
      <p className="mt-2 max-w-md text-sm text-cream/50">A confirmation has been sent to your email. Our team will arrange payment and dispatch shortly.</p>
      <div className="mt-8 flex gap-4">
        <Link to="/shop" className="bg-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright">Continue Shopping</Link>
        <Link to="/profile" className="border border-gold/60 px-8 py-4 text-xs font-bold uppercase tracking-widest text-cream hover:bg-gold/10">View Orders</Link>
      </div>
    </div>
  );
}

export function CheckoutCancel() {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center" data-testid="checkout-cancel">
      <XCircle size={56} className="text-cream/40" />
      <h1 className="mt-6 font-black uppercase tracking-tight text-cream text-4xl">Order Cancelled</h1>
      <p className="mt-3 max-w-md text-sm text-cream/50">Your order was not completed. Your bag has been saved.</p>
      <Link to="/shop" className="mt-8 bg-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright">Return to Store</Link>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 text-center" data-testid="not-found-page">
      <p className="font-serif text-8xl italic text-gold">404</p>
      <h1 className="mt-4 font-black uppercase tracking-tight text-cream text-3xl">Page Not Found</h1>
      <p className="mt-3 text-sm text-cream/50">This piece has moved or never existed.</p>
      <Link to="/" className="mt-8 border border-gold px-8 py-4 text-xs font-bold uppercase tracking-widest text-gold hover:bg-gold hover:text-black">Back Home</Link>
    </div>
  );
}

const WA_TEXT = encodeURIComponent("Hello HYEAMAN, I saw a product I want to purchase.");
const SOCIALS = [
  {
    key: "whatsapp", label: "Contact HYEAMAN on WhatsApp", display: "+1 437-669-3437",
    href: `https://wa.me/14376693437?text=${WA_TEXT}`, accent: true,
    icon: (<svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.82 9.82 0 001.599 5.317l-.999 3.648 3.9-.764zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>),
  },
  {
    key: "instagram", label: "Follow @HYEAMAN_BRAND on Instagram", display: "@HYEAMAN_BRAND",
    href: "https://www.instagram.com/hyeaman_brand/",
    icon: (<svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>),
  },
  {
    key: "tiktok", label: "Follow HYEAMAN on TikTok", display: "TikTok",
    href: "https://www.tiktok.com/@hyeamanclothingbrand?_r=1&_t=ZS-9APr5x4H2j1",
    icon: (<svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>),
  },
  {
    key: "facebook", label: "Follow HYEAMAN on Facebook", display: "Facebook",
    href: "https://www.facebook.com/share/19TpUqW2ev/",
    icon: (<svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>),
  },
];

export function Maintenance() {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-black px-5 py-16 text-center" data-testid="maintenance-page">
      <img src={EDITORIAL.tieMan} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black" />

      <div className="relative w-full max-w-lg">
        <img src="/brand/monogram.jpeg" alt="HYEAMAN" className="mx-auto h-16 w-auto" />
        <p className="mt-5 font-mono text-[0.6rem] uppercase tracking-[0.5em] text-gold/80">HYEAMAN · Street Sheriff</p>

        <h1 className="mt-6 font-black uppercase leading-[0.9] tracking-tight text-cream text-4xl sm:text-5xl">
          Back <span className="font-serif font-normal italic text-gold">Soon</span>
        </h1>
        <p className="mt-4 font-mono text-[0.65rem] uppercase tracking-[0.35em] text-gold/70">Wear Confidence · No One Is Left Behind</p>

        <div className="mx-auto mt-8 max-w-md border-t border-white/10 pt-8">
          <p className="font-serif text-xl italic leading-relaxed text-cream sm:text-2xl" data-testid="maintenance-customer-message">
            Have you seen any Hyeaman product you want to purchase?
          </p>
          <p className="mt-3 text-sm text-cream/60" data-testid="maintenance-invitation">
            Follow us or reach out directly — we'll help you complete your purchase.
          </p>
          <p className="mt-3 text-sm text-cream/60" data-testid="maintenance-follow-note">
            Follow our pages for product drops, photography, modelling shoots, behind-the-scenes content, and more.
          </p>
        </div>

        <nav className="mx-auto mt-8 grid max-w-md grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Contact and social links">
          {SOCIALS.map((s) => (
            <a
              key={s.key}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              data-testid={`maintenance-${s.key}-link`}
              className={`group flex items-center justify-center gap-3 rounded-sm border px-5 py-3.5 text-xs font-bold uppercase tracking-widest transition focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                s.accent
                  ? "border-gold bg-gold text-black hover:bg-gold-bright"
                  : "border-white/15 bg-white/5 text-cream hover:border-gold/60 hover:text-gold"
              }`}
            >
              <span className={s.accent ? "text-black" : "text-gold"}>{s.icon}</span>
              <span>{s.display}</span>
            </a>
          ))}
        </nav>

        <p className="mt-8 text-[0.65rem] uppercase tracking-[0.25em] text-cream/30">HYEAMAN, also known as Street Sheriff</p>
      </div>
    </div>
  );
}
